import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  GitMerge,
  History,
  Lock,
  LockOpen,
  MoreHorizontal,
  Pencil,
  Trash2,
  Undo2
} from 'lucide-react'
import { toast } from 'sonner'
import {
  isMainBranch,
  useProtectBranchMutation,
  useUnprotectBranchMutation,
  type Branch
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'
import { BranchDeleteDialog } from './BranchDeleteDialog'
import { BranchHistoryDrawer } from './BranchHistoryDrawer'
import { BranchRenameDialog } from './BranchRenameDialog'
import { BranchRestoreDialog } from './BranchRestoreDialog'

export interface BranchActionsMenuProps {
  branch: Branch
}

/**
 * Per-row "…" menu for active branches. A protected branch is frozen except
 * for merges into it, so its menu offers Merge / History (+ Unprotect for a
 * user-protected branch); an unprotected branch gets the full set plus Protect
 * and the discard-uncommitted-changes shortcut (restore to head). Main is
 * always protected and can never be unprotected, so its menu drops the protect
 * toggle but keeps Merge / History — and History is where main is restored
 * (which checks the commit out into a new branch rather than rewinding main).
 * Callers should not render this for merged/deleted rows (immutable history in
 * v2 — no purge).
 */
export function BranchActionsMenu({ branch }: BranchActionsMenuProps) {
  const navigate = useNavigate()
  const [historyOpen, setHistoryOpen] = React.useState(false)
  const [renameOpen, setRenameOpen] = React.useState(false)
  const [deleteOpen, setDeleteOpen] = React.useState(false)
  const [restoreOpen, setRestoreOpen] = React.useState(false)
  const [protectBranch, protectState] = useProtectBranchMutation()
  const [unprotectBranch, unprotectState] = useUnprotectBranchMutation()
  const toggling = protectState.isLoading || unprotectState.isLoading

  const handleToggleProtected = async () => {
    try {
      if (branch.protected) {
        await unprotectBranch(branch.id).unwrap()
        toast.success(`${branch.name} is writable again`)
      } else {
        await protectBranch(branch.id).unwrap()
        toast.success(`${branch.name} is now protected — it only changes through merges`)
      }
    } catch {
      toast.error(`Could not ${branch.protected ? 'unprotect' : 'protect'} ${branch.name}`)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={`Actions for branch ${branch.name}`}
          >
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem
            onSelect={() => navigate(`/branches/merge?source=${encodeURIComponent(branch.id)}`)}
          >
            <GitMerge />
            Merge…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setHistoryOpen(true)}>
            <History />
            View history
          </DropdownMenuItem>
          {/* Main is always protected — no toggle. */}
          {isMainBranch(branch) ? null : (
            <DropdownMenuItem disabled={toggling} onSelect={() => void handleToggleProtected()}>
              {branch.protected ? <LockOpen /> : <Lock />}
              {branch.protected ? 'Unprotect branch' : 'Protect branch'}
            </DropdownMenuItem>
          )}
          {!branch.protected ? (
            <>
              <DropdownMenuItem onSelect={() => setRestoreOpen(true)}>
                <Undo2 />
                Discard uncommitted changes
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
                <Pencil />
                Rename branch
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onSelect={() => setDeleteOpen(true)}>
                <Trash2 />
                Delete branch
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <BranchHistoryDrawer branch={branch} open={historyOpen} onOpenChange={setHistoryOpen} />
      <BranchRenameDialog branch={branch} open={renameOpen} onOpenChange={setRenameOpen} />
      <BranchDeleteDialog branch={branch} open={deleteOpen} onOpenChange={setDeleteOpen} />
      <BranchRestoreDialog branch={branch} open={restoreOpen} onOpenChange={setRestoreOpen} />
    </>
  )
}
